import { motion, useAnimate, type AnimationSequence } from 'framer-motion'
import { useCallback, useEffect } from 'react'
import Marquee from 'react-fast-marquee'
import { useTranslate } from '../../../../hooks/language/use-translate'
import { useGlobalStore } from '../../../../store/global-store'
import type { MessageGiftSent } from '../../../../types/lives/messages'

interface Props {
  toast: MessageGiftSent
}

export function Toast({ toast }: Props) {
  const remove = useGlobalStore((state) => state.removeNotifyGiftSentQuantity)

  const [scope, animate] = useAnimate()

  const { translate } = useTranslate()

  useEffect(() => {
    const sequence: AnimationSequence = [
      [scope.current, { opacity: 1 }, { duration: 1.5 }],
    ]

    if (toast?.id) {
      animate(sequence)
    }
  }, [toast?.id, scope, animate, remove])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const sequence: AnimationSequence = [
        [scope.current, { x: -300, opacity: 0 }, { duration: 2, delay: 3 }],
      ]

      animate(sequence).then(() => {
        remove(toast?.id as string)
      })
    }, 2500)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [toast?.quantity])

  const color: () => [string, string] = useCallback(() => {
    const text = toast?.quantity

    if (typeof text !== 'number') return ['#FDFF9A', '#C3FF16']

    switch (true) {
      case text < 5:
        return ['#FFFFFF', '#979797']
      case text < 10:
        return ['#90F378', '#9CF5FB']
      case text < 20:
        return ['#F3EE78', '#9E9CFB']
      case text < 30:
        return ['#B39AFF', '#F35664']
      default:
        return ['#FDFF9A', '#C3FF16']
    }
  }, [toast?.quantity])

  const left = color()[0]
  const right = color()[1]

  const usersReceiveds = toast?.userReceived

  return (
    <motion.div
      ref={scope}
      className="h-16 w-80 flex flex-row rounded-lg items-center ml-4 px-2 text-white opacity-0"
      style={{
        backgroundImage:
          'linear-gradient(to right, #bb5cbe80, #0a157add, transparent)',
      }}
    >
      <img
        src={toast?.invisibleVisitor ? '' : toast?.imgUser}
        className="w-10 h-10 bg-black rounded-full border"
      />

      <div className="ml-2 flex flex-col flex-1 min-w-0">
        <span className="text-sm">
          {toast?.invisibleVisitor
            ? translate('invisible')
            : toast?.userSentName}{' '}
        </span>

        <div style={{ width: '80%' }}>
          <Marquee delay={1} speed={50}>
            <span className="text-sm">
              {translate('quantity-gift-sent-gifted')} {usersReceiveds}{' '}
            </span>
          </Marquee>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-end">
        <img
          src={toast?.gift?.imgGift}
          className="ml-2 w-10 h-10 rounded-full mr-2"
        />

        <div className="w-10">
          <span
            className="colored-text text-3xl bg-clip-text text-transparent"
            style={{
              backgroundImage: `linear-gradient(${left}, ${right})`,
            }}
          >
            {toast?.quantity}x
          </span>
        </div>
      </div>
    </motion.div>
  )
}
